import { useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import {
  type ChangeEvent,
  type ElementRef,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { classList } from '@/utils/cn';
import { NEW_CHAT_ID } from '@/utils/constants';

const threadSelectOptions = (
  threads: {
    pinnedChats: Array<{ id: string; title: string }>;
    recentChats: Array<{ id: string; title: string }>;
    getById: (id: string) => { id: string; title: string } | undefined;
  },
  threadId?: string
) => {
  const byId = new Map<string, { id: string; title: string }>();
  for (const t of threads.pinnedChats) byId.set(t.id, t);
  for (const t of threads.recentChats) byId.set(t.id, t);
  if (threadId && threadId !== NEW_CHAT_ID) {
    const current = threads.getById(threadId);
    if (current) byId.set(current.id, current);
  }
  return [...byId.values()];
};

export const NEW_CHAT_TITLE = 'New Chat';

export interface ChatTitleProps {
  threadId?: string;
}

export const ChatTitle = observer(function ChatTitle({
  threadId,
}: ChatTitleProps) {
  const { threads } = useStores();
  const navigate = useNavigate();
  const measureTitleRef = useRef<ElementRef<'span'>>(null);
  const selectRef = useRef<ElementRef<'select'>>(null);

  const thread = threads.getById(threadId ?? '');
  const title = thread?.title ?? NEW_CHAT_TITLE;

  useEffect(() => {
    if (threadId && threadId !== NEW_CHAT_ID) {
      void threads.ensureLoaded(threadId);
    }
  }, [threadId, threads]);

  const chatSelectOptions = threadSelectOptions(threads, threadId);

  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedChatId = event.target.value;

    navigate({ to: `/chats/${selectedChatId}` });
  };

  const handleOnContextMenu = (event: ReactMouseEvent<HTMLSelectElement>) => {
    event.preventDefault();

    if (selectRef.current) {
      const element = selectRef.current;

      element.focus();

      const clickEvent = new MouseEvent('mousedown');

      element.dispatchEvent(clickEvent);
    }
  };

  useEffect(() => {
    if (measureTitleRef.current && selectRef.current) {
      const { width } = measureTitleRef.current.getBoundingClientRect();

      selectRef.current.style.setProperty('--select-width', `${width + 8}px`);
    }
  }, [selectRef, measureTitleRef]);

  return (
    <div className="relative m-auto flex items-center justify-center gap-2">
      <span
        ref={measureTitleRef}
        className="absolute touch-none pointer-events-none whitespace-nowrap opacity-0"
      >
        {title}
      </span>

      <div>
        <Icons.chat className="text-md transform-gpu h-4 w-4" />
      </div>

      <select
        ref={selectRef}
        className={classList(
          'appearance-none bg-transparent border-none',
          'focus:outline-none focus:ring-0',
          'w-[var(--select-width,auto)]',
          'truncate'
        )}
        name="chats"
        defaultValue={threadId}
        onChange={handleOnChange}
        onContextMenu={handleOnContextMenu}
      >
        {chatSelectOptions.length > 0 && (
          <option key={NEW_CHAT_ID} value={NEW_CHAT_ID}>
            {NEW_CHAT_TITLE}
          </option>
        )}

        {chatSelectOptions.map(({ title, id }) => (
          <option key={id} value={id}>
            {title}
          </option>
        ))}
      </select>
    </div>
  );
});
